import configInfo from 'config';
import { getAwsAuthToken } from 'utils/lib/authHelper';

type QueryRecord = Record<string, string | number | boolean | undefined | null>;

const buildRootUrl = (): string => {
  const base = String(configInfo.apiGateway.AI_BASE_NODE_URL || '').replace(/\/$/, '');
  // const base = 'http://localhost:4000';
  return `${base}/translation-practice`;
};

const parseTranslationPracticeResponse = async <T>(res: Response, method: string, path: string): Promise<T | null> => {
  if (!res.ok) {
    throw new Error(`translation-practice ${method} ${path}: ${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
};

const buildAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getAwsAuthToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'x-auth-source': configInfo.isDev ? 'test' : 'prod',
  };
};

export async function translationPracticePostList<T = unknown>(
  payload: QueryRecord = {}
): Promise<T | null> {
  const url = `${buildRootUrl()}/list`;
  const res = await fetch(url, {
    method: 'POST',
    headers: await buildAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return parseTranslationPracticeResponse<T>(res, 'POST', '/list');
}

export async function translationPracticeRequest<T = unknown>(
  path: string,
  options: { method?: string; body?: unknown; query?: QueryRecord } = {}
): Promise<T | null> {
  const { method = 'GET', body, query } = options;
  const normalizedMethod = method.toUpperCase();
  let url = `${buildRootUrl()}${path.startsWith('/') ? path : `/${path}`}`;
  if (normalizedMethod === 'GET' && query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      params.set(key, String(value));
    });
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  const hasBody = normalizedMethod !== 'GET' && normalizedMethod !== 'HEAD';
  const res = await fetch(url, {
    method: normalizedMethod,
    headers: await buildAuthHeaders(),
    body: hasBody ? JSON.stringify(body ?? {}) : undefined,
  });
  return parseTranslationPracticeResponse<T>(res, normalizedMethod, path);
}
